import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Matter, Sponsor, MatterHistory, MatterAttachment } from '../../types/legislation';
import { getAttachmentFileUrl } from '../../services/legislationApi';
import rtfToText from '../../utils/rtfToText';
import ActionTimeline, { EventAction } from './ActionTimeline';

interface MatterGroupCardProps {
  matter: Matter;
  sponsors: Sponsor[];
  histories: MatterHistory[];
  attachments: MatterAttachment[];
  actions: EventAction[];
  eventId: string;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusColor = (status: string): string => {
  const s = status.toLowerCase();
  if (s.includes('enacted') || s.includes('adopted')) return 'bg-green-100 text-green-800';
  if (s.includes('introduced')) return 'bg-blue-100 text-blue-800';
  if (s.includes('committee')) return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
};

const MatterGroupCard: React.FC<MatterGroupCardProps> = ({
  matter,
  sponsors,
  histories,
  attachments,
  actions,
  eventId,
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);

  // Derive the bill's version at this meeting from event item versions
  // Priority: A > B > C > ... > * (original)
  const billVersion = actions.reduce<string | null>((best, action) => {
    const v = action.EventItemVersion;
    if (!v) return best;
    if (!best) return v;
    // Letters (A, B, C...) take priority over * (original)
    if (best === '*') return v === '*' ? best : v;
    if (v === '*') return best;
    return v > best ? v : best;
  }, null);

  // Categorize attachments by type
  const categorize = (att: MatterAttachment) => {
    const name = (att.MatterAttachmentName || att.MatterAttachmentFileName || '').toLowerCase();

    // Bill text versions — match patterns like "int. no. 111", "proposed int.", "res. no.", "local law"
    if (
      /\b(int\.|res\.|resolution)\s*(no\.?)?\s*\d/i.test(name) ||
      name.includes('(final)') ||
      /^local law\b/.test(name)
    ) return 'billText';

    // Summaries
    if (/^summary\b/.test(name)) return 'summary';

    // Testimony & public comments
    if (name.includes('testimony') || name.includes('statement') || name.includes('comment'))
      return 'testimony';

    // Committee reports
    if (name.includes('committee report')) return 'report';

    // Hearing transcripts & minutes
    if (name.includes('transcript') || name.includes('minutes')) return 'transcript';

    // Fiscal impact
    if (name.includes('fiscal impact')) return 'fiscal';

    return 'other';
  };

  const groups: Record<string, MatterAttachment[]> = {};
  attachments.forEach((att) => {
    const cat = categorize(att);
    (groups[cat] = groups[cat] || []).push(att);
  });

  // Display order for categories
  const categoryMeta: { key: string; label: string; color: string; bg: string }[] = [
    { key: 'billText', label: 'Bill Text Versions', color: 'text-nyc-blue', bg: 'bg-blue-50' },
    { key: 'summary', label: 'Summaries', color: 'text-indigo-700', bg: '' },
    { key: 'testimony', label: 'Testimony & Public Comments', color: 'text-green-700', bg: 'bg-green-50' },
    { key: 'report', label: 'Committee Reports', color: 'text-nyc-orange', bg: '' },
    { key: 'transcript', label: 'Transcripts & Minutes', color: 'text-gray-700', bg: '' },
    { key: 'fiscal', label: 'Fiscal Impact', color: 'text-gray-700', bg: '' },
    { key: 'other', label: 'Other Documents', color: 'text-gray-600', bg: '' },
  ];

  const renderAttachmentLink = (
    attachment: MatterAttachment,
    className: string
  ) => {
    const fileNameLower = attachment.MatterAttachmentFileName?.toLowerCase() || '';
    const isViewable = fileNameLower.endsWith('.pdf') || fileNameLower.endsWith('.docx');
    const docName = encodeURIComponent(
      attachment.MatterAttachmentFileName || attachment.MatterAttachmentName || 'Document'
    );

    if (isViewable) {
      return (
        <Link
          to={`/event/${eventId}/document/${matter.MatterId}/${attachment.MatterAttachmentId}/${docName}`}
          className={className}
        >
          {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName || 'Document'}
        </Link>
      );
    }

    return (
      <a
        href={getAttachmentFileUrl(matter.MatterId, attachment.MatterAttachmentId)}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {attachment.MatterAttachmentName || attachment.MatterAttachmentFileName || 'Document'}
      </a>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white">
      {/* Header: bill file + title */}
      <div className="mb-3">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
          <Link
            to={`/legislation/${matter.MatterId}`}
            className="text-lg font-semibold text-nyc-blue hover:underline"
          >
            {matter.MatterFile}
          </Link>
          <div className="flex flex-wrap gap-1.5">
            {billVersion && billVersion !== '*' && (
              <span
                className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 font-medium"
                title={`Amended version ${billVersion}`}
              >
                Ver. {billVersion}
              </span>
            )}
            {billVersion === '*' && (
              <span
                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 font-medium"
                title="Original version"
              >
                Ver. *
              </span>
            )}
            {matter.MatterTypeName && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                {matter.MatterTypeName}
              </span>
            )}
            {matter.MatterStatusName && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(matter.MatterStatusName)}`}>
                {matter.MatterStatusName}
              </span>
            )}
            {matter.MatterBodyName && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                {matter.MatterBodyName}
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-800 text-sm">
          {matter.MatterTitle || matter.MatterName}
        </p>
      </div>

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {sponsors
            .sort((a, b) => a.MatterSponsorSequence - b.MatterSponsorSequence)
            .map((sponsor, index) => (
              <span key={sponsor.MatterSponsorId} className="inline-flex items-center gap-1.5 text-sm">
                <span
                  className={`px-1.5 py-0.5 text-xs rounded ${
                    index === 0 ? 'bg-nyc-blue text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {index === 0 ? 'Primary' : 'Co-sponsor'}
                </span>
                <span className="text-gray-800">{sponsor.MatterSponsorName}</span>
              </span>
            ))}
        </div>
      )}

      {/* Key dates — derived fields (mirrors LegislationDetail), not the legacy
          MatterPassedDate/MatterEnactmentDate which can collapse and hide the vote */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 mb-4">
        {matter.MatterIntroDate && (
          <span>Introduced: {formatDate(matter.MatterIntroDate)}</span>
        )}
        {matter.CouncilVoteDate && (
          <span>Council Vote: {formatDate(matter.CouncilVoteDate)}</span>
        )}
        {matter.EnactmentDate && (
          <span>{matter.EnactmentLabel || 'Enacted'}: {formatDate(matter.EnactmentDate)}</span>
        )}
      </div>

      {/* Action timeline — the primary new content */}
      <div className="mb-4">
        <ActionTimeline actions={actions} />
      </div>

      {/* Categorized attachments */}
      {attachments.length > 0 && (
        <div className="space-y-3 mb-4">
          {categoryMeta
            .filter((cat) => groups[cat.key]?.length)
            .map((cat) => (
              <div key={cat.key} className={cat.bg ? `p-3 ${cat.bg} rounded-lg` : ''}>
                <h4 className={`text-sm font-semibold mb-1.5 ${cat.color}`}>{cat.label}</h4>
                <div className="space-y-1">
                  {groups[cat.key].map((att) => (
                    <div key={att.MatterAttachmentId}>
                      {renderAttachmentLink(att, `${cat.color} hover:underline text-sm`)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Collapsible legislative history */}
      {histories.length > 0 && (
        <div>
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            <span className={`transition-transform ${historyOpen ? 'rotate-90' : ''}`}>&#9654;</span>
            Full Legislative History ({histories.length})
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-200">
              {histories
                .sort((a, b) => new Date(b.MatterHistoryActionDate).getTime() - new Date(a.MatterHistoryActionDate).getTime())
                .map((h) => (
                  <div key={h.MatterHistoryId} className="text-sm">
                    <span className="text-gray-500">
                      {new Date(h.MatterHistoryActionDate).toLocaleDateString()}
                    </span>
                    <span className="ml-2 font-medium text-gray-800">{h.MatterHistoryActionName}</span>
                    {h.MatterHistoryActionBodyName && (
                      <span className="ml-1 text-gray-500">- {h.MatterHistoryActionBodyName}</span>
                    )}
                    {h.MatterHistoryPassedFlag === 1 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">Pass</span>
                    )}
                    {h.MatterHistoryActionText && (
                      <p className="text-gray-600 mt-0.5">{rtfToText(h.MatterHistoryActionText)}</p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatterGroupCard;
